import React, { useState } from "react";
import { useTheme } from "../../../../contexts/ThemeContext.js";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faCircleCheck, faTriangleExclamation, faCheckCircle, faPlus, faPen, faTrash, faShieldCheck } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface PaymentCard {
  id: string;
  last4: string;
  brand: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

export default function BillingSection() {
  const { isDark } = useTheme();
  const [cards, setCards] = useState<PaymentCard[]>([
    {
      id: 'card_1',
      last4: '4242',
      brand: 'Visa',
      expMonth: 12,
      expYear: 2025,
      isDefault: true
    },
    {
      id: 'card_2',
      last4: '5555',
      brand: 'Mastercard',
      expMonth: 10,
      expYear: 2026,
      isDefault: false
    }
  ]);
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [newCardDetails, setNewCardDetails] = useState({
    cardNumber: '',
    cardName: '',
    expiry: '',
    cvc: ''
  });

  const handleSetDefaultCard = (cardId: string) => {
    setCards(cards.map(card => ({
      ...card,
      isDefault: card.id === cardId
    })));
  };

  const handleAddCard = () => {
    // In a real implementation, this would integrate with a payment processor
    // For now, we'll just simulate adding a card
    const newCard: PaymentCard = {
      id: `card_${Date.now()}`,
      last4: newCardDetails.cardNumber.slice(-4),
      brand: newCardDetails.cardNumber.startsWith('4') ? 'Visa' : 'Mastercard',
      expMonth: parseInt(newCardDetails.expiry.split('/')[0]),
      expYear: parseInt(`20${newCardDetails.expiry.split('/')[1]}`),
      isDefault: false
    };
    
    setCards([...cards, newCard]);
    setIsAddingCard(false);
    setNewCardDetails({
      cardNumber: '',
      cardName: '',
      expiry: '',
      cvc: ''
    });
  };

  const getBrandIcon = (brand: string) => {
    if (brand.toLowerCase() === 'visa') {
      return '💳'; // Replace with actual Visa icon in production
    } else if (brand.toLowerCase() === 'mastercard') {
      return '💳'; // Replace with actual Mastercard icon in production
    } else {
      return '💳';
    }
  };

  return (
    <div className="space-y-6">
      {/* Billing Email */}
      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6">
        <h2 className="text-xl font-semibold mb-4 text-foreground">Billing Settings</h2>
        <p className="text-sm text-muted-foreground mb-6">Manage your billing settings and payment information.</p>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="billingEmail" className="block text-sm font-medium text-foreground">
              Accounts Email
            </label>
            <input
              type="email"
              id="billingEmail"
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              placeholder="Enter accounts email"
            />
            <p className="text-xs text-muted-foreground">
              This is your accounts team's email<br />
              For example, sales@deiterate.com
            </p>
          </div>
          
          <div className="pt-4">
            <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors">
              Save
            </button>
          </div>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6 mb-6">
        <div className="font-semibold text-lg mb-4 text-foreground">Payment Methods</div>
        
        {cards.length > 0 ? (
          <div className="space-y-4 mb-6">
            {cards.map((card) => (
              <div 
                key={card.id} 
                className={`p-4 rounded-lg border ${
                  card.isDefault 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border bg-background'
                } transition-colors`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{getBrandIcon(card.brand)}</div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-foreground">
                          {card.brand} •••• {card.last4}
                        </p>
                        {card.isDefault && (
                          <span className="bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full font-medium">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Expires {card.expMonth}/{card.expYear}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {!card.isDefault && (
                      <button 
                        onClick={() => handleSetDefaultCard(card.id)}
                        className="p-2 bg-background hover:bg-accent text-foreground rounded-lg transition-colors"
                      >
                        <FontAwesomeIcon icon={faCheckCircle} className="text-sm" />
                      </button>
                    )}
                    <button className="p-2 bg-background hover:bg-red-500/10 text-red-500 rounded-lg transition-colors">
                      <FontAwesomeIcon icon={faTrash} className="text-sm" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 border border-dashed border-border rounded-lg mb-6">
            <FontAwesomeIcon icon={faCreditCard} className="text-muted-foreground text-3xl mb-2" />
            <p className="text-muted-foreground">No payment methods added yet</p>
          </div>
        )}
        
        <Dialog open={isAddingCard} onOpenChange={setIsAddingCard}>
          <DialogTrigger asChild>
            <button className="flex items-center gap-2 border border-border rounded-lg px-4 py-2 bg-card hover:bg-accent text-foreground font-medium text-sm shadow-sm">
              <span className="bg-primary/10 px-2 py-1 rounded">
                <FontAwesomeIcon icon={faPlus} className="text-primary" />
              </span>
              Add Payment Method
            </button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md bg-white dark:bg-gray-900 border-border">
            <DialogHeader>
              <DialogTitle>Add Payment Method</DialogTitle>
              <DialogDescription>
                Add a new credit or debit card to your account.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label htmlFor="cardNumber" className="text-sm font-medium">
                  Card Number
                </label>
                <Input
                  id="cardNumber"
                  placeholder="1234 5678 9012 3456"
                  value={newCardDetails.cardNumber}
                  onChange={(e) => setNewCardDetails({...newCardDetails, cardNumber: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="cardName" className="text-sm font-medium">
                  Name on Card
                </label>
                <Input
                  id="cardName"
                  placeholder="John Doe"
                  value={newCardDetails.cardName}
                  onChange={(e) => setNewCardDetails({...newCardDetails, cardName: e.target.value})}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="expiry" className="text-sm font-medium">
                    Expiry Date
                  </label>
                  <Input
                    id="expiry"
                    placeholder="MM/YY"
                    value={newCardDetails.expiry}
                    onChange={(e) => setNewCardDetails({...newCardDetails, expiry: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="cvc" className="text-sm font-medium">
                    CVC
                  </label>
                  <Input
                    id="cvc"
                    placeholder="123"
                    value={newCardDetails.cvc}
                    onChange={(e) => setNewCardDetails({...newCardDetails, cvc: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
                <FontAwesomeIcon icon={faShieldCheck} className="text-green-500" />
                Your payment information is secure and encrypted
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddingCard(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddCard}>
                Add Card
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Subscription Status */}
      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6">
        <div className="font-semibold text-lg mb-4 text-foreground">Subscription Status</div>
        
        <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg mb-4">
          <FontAwesomeIcon icon={faCircleCheck} className="text-green-600 dark:text-green-400 text-xl" />
          <div>
            <p className="text-sm font-medium text-green-800 dark:text-green-300">Active Subscription</p>
            <p className="text-xs text-green-700 dark:text-green-400">Your subscription is active and will renew on May 15, 2025.</p>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <button className="px-4 py-2 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors">
            Pause Subscription
          </button>
          <button className="px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors">
            Cancel Subscription
          </button>
        </div>
      </div>

      {/* Stripe Portal */}
      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6">
        <div className="font-semibold text-lg mb-4 text-foreground">Billing Portal</div>
        <p className="text-sm text-muted-foreground mb-4">
          Access the Stripe billing portal to view your invoices, update payment methods, and manage your subscription.
        </p>
        <button className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg bg-background hover:bg-accent text-foreground font-medium transition-colors">
          Access Stripe Portal
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1">
            <path d="M7 17l9.2-9.2M17 17V7H7" />
          </svg>
        </button>
      </div>
    </div>
  );
}
